import { startCase } from 'lodash-es'

/**
 * Centralized carrier matching registry. Each entry contains regex matchers for
 * identifying a carrier from raw strings (typically Shopify fulfillment
 * trackingInfo[].company). Used by identifyCarrier() and the helpers that
 * depend on it (tracking URLs, drop-off URLs, brand notices).
 * Note: formatCarrierName() uses its own CARRIER_DISPLAY_NAMES map for richer
 * display-name resolution (e.g. "Amazon Logistics" as a distinct entry).
 */
const CARRIER_MATCHERS = {
  ups: { matchers: [/\bups\b/i] },
  usps: { matchers: [/usps/i, /united states postal/i] },
  fedex: { matchers: [/fedex/i, /fed ex/i, /federal express/i] },
  dhl: { matchers: [/dhl/i] },
  ontrac: { matchers: [/ontrac/i] },
  lasership: { matchers: [/lasership/i] },
  amazon: { matchers: [/amazon/i] },
}

/**
 * Identifies a carrier key from a raw string using the centralized matchers.
 * @param {string|null|undefined} carrier - Raw carrier name
 * @returns {string|null} Carrier key (e.g. 'fedex', 'ups') or null
 */
export function identifyCarrier(carrier) {
  if (!carrier || typeof carrier !== 'string') return null
  const trimmed = carrier.trim()
  if (!trimmed) return null

  for (const [key, entry] of Object.entries(CARRIER_MATCHERS)) {
    if (entry.matchers.some(re => re.test(trimmed))) {
      return key
    }
  }
  return null
}

/**
 * Maps carrier names to their properly formatted display names
 */
const CARRIER_DISPLAY_NAMES = {
  ups: 'UPS',
  usps: 'USPS',
  fedex: 'FedEx',
  'fed ex': 'FedEx',
  dhl: 'DHL',
  ontrac: 'OnTrac',
  lasership: 'LaserShip',
  amazon: 'Amazon',
  'amazon logistics': 'Amazon Logistics',
}

/**
 * Formats carrier name for display (handles all-caps input like "FEDEX" -> "FedEx")
 * @param {string} carrier - The shipping carrier name
 * @returns {string} The properly formatted carrier name
 */
export function formatCarrierName(carrier) {
  if (!carrier) return ''

  const normalized = carrier.toLowerCase().trim()

  // Check for exact match first
  if (CARRIER_DISPLAY_NAMES[normalized]) {
    return CARRIER_DISPLAY_NAMES[normalized]
  }

  // Check for partial matches
  for (const [key, displayName] of Object.entries(CARRIER_DISPLAY_NAMES)) {
    if (normalized.includes(key)) {
      return displayName
    }
  }

  // If no match found, return title case version of original
  return startCase(carrier)
}

/**
 * Generates a carrier-specific tracking URL based on carrier name and tracking number
 * @param {string} carrier - The shipping carrier name
 * @param {string} trackingNumber - The tracking number
 * @returns {string|null} The tracking URL or null if carrier is not supported
 */
export function getCarrierTrackingUrl(carrier, trackingNumber) {
  if (!carrier || !trackingNumber) return null

  const carrierKey = identifyCarrier(carrier)
  if (!carrierKey) return null

  const num = encodeURIComponent(trackingNumber)

  switch (carrierKey) {
    case 'ups':
      return `https://www.ups.com/track?tracknum=${num}`
    case 'usps':
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${num}`
    case 'fedex':
      return `https://www.fedex.com/fedextrack/?trknbr=${num}`
    case 'dhl':
      return `https://www.dhl.com/en/express/tracking.html?AWB=${num}`
    case 'ontrac':
      return `https://www.ontrac.com/tracking/?number=${num}`
    case 'lasership':
      return `https://www.lasership.com/track/${num}`
    case 'amazon':
      return `https://track.amazon.com/tracking/${num}`
    default:
      return null
  }
}

const TRACKING_STATUS_COLORS = {
  delivered: 'positive',
  in_transit: 'info',
  out_for_delivery: 'info',
  pre_transit: 'warning',
  available_for_pickup: 'warning',
  failure: 'negative',
  return_to_sender: 'negative',
  cancelled: 'negative',
  error: 'negative',
  unknown: 'grey-6',
}

/**
 * Returns the badge color for a tracking status string
 * @param {string|null} status
 * @returns {string} Quasar color name
 */
export function getTrackingStatusColor(status) {
  if (!status) return 'grey-6'
  return TRACKING_STATUS_COLORS[status] || 'grey-6'
}

/**
 * Returns a human-readable label for a tracking status string
 * @param {string|null} status
 * @returns {string}
 */
export function getTrackingStatusLabel(status) {
  if (!status) return 'Awaiting Update'
  return startCase(status.replace(/_/g, ' ').toLowerCase())
}

/**
 * Formats tracking info from shipment boxes for display
 * @param {Array} shipmentBoxes - Array of shipment box objects with carrier and trackingNumber
 * @returns {Array} Array of formatted tracking info with url, carrier, and number
 */
export function formatTrackingInfo(shipmentBoxes) {
  if (!Array.isArray(shipmentBoxes) || shipmentBoxes.length === 0) {
    return []
  }

  return shipmentBoxes
    .map(box => ({
      id: box.id,
      carrier: formatCarrierName(box.carrier),
      number: box.trackingNumber,
      url: getCarrierTrackingUrl(box.carrier, box.trackingNumber),
    }))
    .filter(info => info.number) // Filter out entries without tracking numbers
}

/**
 * Generates a carrier-specific drop-off locator URL.
 * Returns null for carriers without a known locator (OnTrac, LaserShip, Amazon).
 *
 * @param {string} carrier - The shipping carrier name
 * @returns {string|null} The drop-off locator URL or null if unsupported
 */
const DROP_OFF_URLS = {
  ups: 'https://www.ups.com/dropoff',
  fedex: 'https://www.fedex.com/locate',
  usps: 'https://tools.usps.com/find-location.htm',
  dhl: 'https://locator.dhl.com/',
}

export function getCarrierDropOffUrl(carrier) {
  if (!carrier) return null

  const carrierKey = identifyCarrier(carrier)

  // Unsupported: OnTrac, LaserShip, Amazon — no public drop-off locator
  return DROP_OFF_URLS[carrierKey] ?? null
}

/**
 * Map of carrier brand keys to display metadata used for compliance/brand
 * notices on the return flow's Shipping Box Details page.
 *
 * Structured as a registry so additional carrier brand notices can be added in
 * future stories (UPS / USPS / DHL) by appending entries here and adding the
 * corresponding logo asset under public/img/carriers/.
 *
 * Matching is handled by the shared `identifyCarrier()` helper — no separate
 * matchers needed here.
 */
export const CARRIER_BRAND_INFO = {
  fedex: {
    logoSrc: '/img/carriers/fedex.svg',
    logoAlt: 'FedEx',
    legalNotice: 'FedEx service marks are owned by Federal Express Corporation and are used by permission.',
  },
}

/**
 * Identifies a carrier brand from a raw carrier string (typically from a
 * Shopify fulfillment's trackingInfo[].company). Uses the centralized
 * `identifyCarrier` matcher so rules are consistent across the app.
 *
 * @param {string|null|undefined} carrier - Raw carrier name (e.g. Shopify tracking company)
 * @returns {object|null} Brand metadata entry from CARRIER_BRAND_INFO, or null if no brand is registered
 */
export function getCarrierBrand(carrier) {
  const key = identifyCarrier(carrier)
  if (!key || !CARRIER_BRAND_INFO[key]) return null
  return { key, ...CARRIER_BRAND_INFO[key] }
}

/**
 * Convenience predicate — true if the supplied carrier identifies as FedEx.
 * Uses the centralized identifyCarrier matcher directly, independent of the
 * brand-notice registry.
 * @param {string|null|undefined} carrier
 * @returns {boolean}
 */
export function isFedExCarrier(carrier) {
  return identifyCarrier(carrier) === 'fedex'
}
